/**
 * Full SSR (treatment) entry for the user-profile artworks tab.
 * Mounted via `dynamicWithFeatureFlag` from
 * `routes/_app.$usernameRaw.artworks.($subTab).tsx` whenever the
 * global `SSR_DUAL_MODE_EXPERIMENT_NAME` experiment resolves to
 * `treatment`.
 *
 * Behaviour:
 * - Reads `loaderData.artworksReactQuerySeed` produced by the
 *   loader's `fetchUserArtworksLatestForSSR(...)` and writes it
 *   into the react-query cache before mount, so
 *   `<BaseCursorPaging useSWR={useListUserArtworks}>` reads cache
 *   on first render and emits the real 20-card grid into `<main>`
 *   without waiting on a live `/v2/user/<id>/artworks/latest`
 *   request.
 * - The previous incarnation seeded Apollo's `ListArtworks` cache
 *   instead, but main migrated the live grid hook from Apollo's
 *   `useListArtworks` to oRPC's `useListUserArtworks` (PR #10763
 *   `aef989c98`), so the Apollo seed never reached the live hook
 *   any more — the SSR HTML shipped an empty `<main>` and JS-
 *   disabled crawlers saw zero `<img>` tags on `/zh/@<user>/
 *   artworks`. Seeding react-query directly closes that gap.
 *
 * The CSR (control) entry lives in
 * `_app.$usernameRaw.artworks.($subTab).csr.tsx` and skips the seed
 * entirely — the live grid renders its skeleton until the
 * react-query response resolves, equivalent to Stage 1 behaviour.
 */
import { useState } from 'react'
import { useLoaderData } from 'react-router'
import ClientUserArtworksPage from 'src/clientRoutes/_app.$usernameRaw.artworks.($subTab)'
import type { loader } from 'src/routes/_app.$usernameRaw.artworks.($subTab)'
import { orpc } from 'src/services/orpc/contract'
import { useRequestContext } from 'src/utils/request-context'

export default function UserArtworksFullSSREntry() {
  const loaderData = useLoaderData<typeof loader>()
  const { queryClient } = useRequestContext()

  const seed =
    'artworksReactQuerySeed' in loaderData
      ? loaderData.artworksReactQuerySeed
      : null

  // `useState(() => {...})` runs the initialiser exactly once per
  // mount (server + first client commit), which matches the home
  // route's `_app._index.full-ssr.tsx` pattern. The `if
  // (queryClient.getQueryData(queryKey))` guard makes this a no-op
  // when the cache already holds the data, so re-renders triggered
  // by route loader re-deserialisation don't repeatedly clobber the
  // live query response.
  //
  // CRUCIAL: build the queryKey via `orpc.user.getUserArtworksLatest.
  // queryOptions(...)` — the same helper the live
  // `useListUserArtworks` hook calls inside
  // `useQuery({ ...orpc.user.getUserArtworksLatest.queryOptions({
  // input }) })`. The SDK's `userGetUserArtworksLatestQueryKey`
  // helper produces a *different* key shape, so seeding with it
  // would land in a cache bucket the live hook never reads.
  useState(() => {
    if (!seed) return null
    const queryKey = orpc.user.getUserArtworksLatest.queryOptions({
      input: seed.input,
    }).queryKey
    if (queryClient.getQueryData(queryKey)) return null
    // Type-cast: the loader stores the SDK-generated response shape
    // (`UserGetUserArtworksLatestResponse` from `@hey-api/openapi-ts`)
    // while `orpc.user.getUserArtworksLatest.queryOptions(...)`
    // narrows to the contract-typed shape from `pixai-contract`. At
    // runtime the server emits one identical JSON payload for both
    // clients (the SDK + contract share the same OpenAPI spec), so
    // writing the SDK type into the contract-keyed cache slot is
    // safe — any optional field that differs between the two type
    // generators reads as `undefined` until the live refetch lands.
    queryClient.setQueryData(queryKey, seed.rawResponse as never)
    return null
  })

  return <ClientUserArtworksPage />
}
